import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { CreditCard, Plus, Lightning, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { BalanceCard, LinkWallet } from './wallet/BalanceCard';
import StakingPanel from './wallet/StakingPanel';
import { OnChainBalance, MintToWallet, SendBoltz } from './wallet/SolanaOps';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WalletPage() {
    const { user, updateUser } = useAuth();
    const [searchParams] = useSearchParams();
    const [walletInfo, setWalletInfo] = useState(null);
    const [balanceInfo, setBalanceInfo] = useState(null);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [linking, setLinking] = useState(false);
    const [purchasing, setPurchasing] = useState(null);
    const [minting, setMinting] = useState(false);
    const [mintAmount, setMintAmount] = useState('');
    const [walletAddress, setWalletAddress] = useState('');
    const [copied, setCopied] = useState(false);
    const [transferTo, setTransferTo] = useState('');
    const [transferAmount, setTransferAmount] = useState('');
    const [transferring, setTransferring] = useState(false);
    const [solanaStatus, setSolanaStatus] = useState(null);
    const [stakingTiers, setStakingTiers] = useState([]);
    const [stakingPositions, setStakingPositions] = useState([]);
    const [stakeAmount, setStakeAmount] = useState('');
    const [stakeTier, setStakeTier] = useState('7d');
    const [staking, setStaking] = useState(false);
    const [unstaking, setUnstaking] = useState(null);

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId) pollPaymentStatus(sessionId);
    }, [searchParams]);

    useEffect(() => { fetchWalletData(); fetchPackages(); fetchSolanaStatus(); fetchStaking(); }, []);

    const fetchSolanaStatus = async () => {
        try { const res = await fetch(`${API}/solana/status`, { credentials: 'include' }); if (res.ok) setSolanaStatus(await res.json()); } catch (e) { console.error('Solana:', e); }
    };

    const fetchStaking = async () => {
        try {
            const [tiersRes, posRes] = await Promise.all([fetch(`${API}/staking/tiers`, { credentials: 'include' }), fetch(`${API}/staking/positions`, { credentials: 'include' })]);
            if (tiersRes.ok) setStakingTiers(await tiersRes.json());
            if (posRes.ok) setStakingPositions(await posRes.json());
        } catch (e) { console.error('Staking:', e); }
    };

    const stakeBoltz = async () => {
        const amount = parseFloat(stakeAmount);
        if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
        setStaking(true);
        try {
            const res = await fetch(`${API}/staking/stake`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ amount, tier: stakeTier }) });
            if (res.ok) { const d = await res.json(); toast.success(`Staked Boltz for ${d.tier}!`); updateUser({ boltz_balance: d.new_balance }); setStakeAmount(''); fetchStaking(); fetchWalletData(); }
            else { const e = await res.json(); toast.error(e.detail || 'Staking failed'); }
        } catch { toast.error('Staking failed'); } finally { setStaking(false); }
    };

    const unstakeBoltz = async (stakeId) => {
        setUnstaking(stakeId);
        try {
            const res = await fetch(`${API}/staking/${stakeId}/unstake`, { method: 'POST', credentials: 'include' });
            if (res.ok) { const d = await res.json(); d.status === 'early_unstaked' ? toast.warning(`Early unstake: ${d.penalty} Boltz penalty`) : toast.success(`Unstaked! +${d.reward} Boltz reward`); updateUser({ boltz_balance: d.new_balance }); fetchStaking(); fetchWalletData(); }
            else { const e = await res.json(); toast.error(e.detail || 'Unstaking failed'); }
        } catch { toast.error('Unstaking failed'); } finally { setUnstaking(null); }
    };

    const fetchWalletData = async () => {
        try {
            const [infoRes, balanceRes] = await Promise.all([fetch(`${API}/wallet/info`, { credentials: 'include' }), fetch(`${API}/wallet/balance`, { credentials: 'include' })]);
            if (infoRes.ok) setWalletInfo(await infoRes.json());
            if (balanceRes.ok) setBalanceInfo(await balanceRes.json());
        } catch (e) { console.error('Wallet:', e); } finally { setLoading(false); }
    };

    const fetchPackages = async () => {
        try { const res = await fetch(`${API}/payments/packages`, { credentials: 'include' }); if (res.ok) setPackages(await res.json()); } catch (e) { console.error('Packages:', e); }
    };

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        if (attempts >= 10) { toast.info('Payment status check timed out. Boltz will be credited shortly.'); return; }
        try {
            const res = await fetch(`${API}/payments/status/${sessionId}`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                if (data.payment_status === 'paid') { toast.success('Payment successful! Boltz added.'); fetchWalletData(); window.history.replaceState({}, '', '/wallet'); return; }
                if (data.status === 'expired') { toast.error('Payment session expired.'); window.history.replaceState({}, '', '/wallet'); return; }
            }
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
        } catch (e) { console.error('Poll:', e); }
    };

    const purchasePackage = async (packageId) => {
        setPurchasing(packageId);
        try {
            const res = await fetch(`${API}/payments/checkout`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }) });
            if (res.ok) { const d = await res.json(); if (d.checkout_url || d.url) window.location.href = d.checkout_url || d.url; }
            else { const e = await res.json(); toast.error(e.detail || 'Failed to initiate payment'); }
        } catch { toast.error('Failed to initiate payment'); } finally { setPurchasing(null); }
    };

    const linkWallet = async () => {
        if (!walletAddress || walletAddress.length < 32) { toast.error('Enter a valid Solana wallet address'); return; }
        setLinking(true);
        try {
            const res = await fetch(`${API}/wallet/link`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ walletAddress }) });
            if (res.ok) { const r = await res.json(); updateUser({ wallet_address: r.walletAddress }); toast.success('Wallet linked!'); fetchWalletData(); }
            else { const e = await res.json(); toast.error(e.detail || 'Failed to link wallet'); }
        } catch { toast.error('Failed to link wallet'); } finally { setLinking(false); }
    };

    const copyAddress = async () => {
        if (user?.wallet_address) { await navigator.clipboard.writeText(user.wallet_address); setCopied(true); toast.success('Address copied!'); setTimeout(() => setCopied(false), 2000); }
    };

    const mintTokens = async () => {
        const amount = parseFloat(mintAmount);
        if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
        if (amount > (user?.boltz_balance || 0)) { toast.error('Insufficient balance'); return; }
        setMinting(true);
        try {
            const res = await fetch(`${API}/solana/mint`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ amount }) });
            if (res.ok) { const d = await res.json(); toast.success(d.message); updateUser({ boltz_balance: d.new_in_app_balance }); setMintAmount(''); fetchWalletData(); }
            else { const e = await res.json(); toast.error(e.detail || 'Minting failed'); }
        } catch { toast.error('Failed to mint tokens'); } finally { setMinting(false); }
    };

    const transferTokens = async () => {
        if (!transferTo || transferTo.length < 32) { toast.error('Enter a valid address'); return; }
        const amount = parseFloat(transferAmount);
        if (!amount || amount <= 0 || amount > (user?.boltz_balance || 0)) { toast.error('Invalid amount'); return; }
        setTransferring(true);
        try {
            const res = await fetch(`${API}/solana/transfer`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ to_wallet: transferTo, amount }) });
            if (res.ok) { const d = await res.json(); toast.success(`Sent ${amount} Boltz`); updateUser({ boltz_balance: d.new_balance }); setTransferTo(''); setTransferAmount(''); fetchWalletData(); }
            else { const e = await res.json(); toast.error(e.detail || 'Transfer failed'); }
        } catch { toast.error('Transfer failed'); } finally { setTransferring(false); }
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-primary text-glow">WALLET</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground">Boltz Wallet</h1>
                    <p className="text-muted-foreground mt-1" style={{ fontFamily: 'var(--font-body)' }}>Manage your Boltz tokens and Solana wallet</p>
                        <div className="mt-3 rounded-xl border border-boltz-gold/40 bg-boltz-gold/10 px-3 py-2" data-testid="devnet-demo-banner">
                            <p className="text-xs text-boltz-gold font-display">DEVNET DEMO &mdash; BOLTZ MINTING IS NOT LIVE</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5">On-chain actions run against Solana devnet for demonstration. Balances and staking rewards are simulated and have no monetary value.</p>
                        </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <BalanceCard user={user} balanceInfo={balanceInfo} loading={loading} copied={copied} copyAddress={copyAddress} />
                </motion.div>

                {/* Buy Boltz */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                    <Card className="glass-card mb-6">
                        <CardHeader><CardTitle className="flex items-center gap-2"><CreditCard size={20} />Buy Boltz Coins</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid gap-4">
                                {packages.map((pkg) => (
                                    <div key={pkg.id} className="flex items-center justify-between p-4 border border-border rounded-xl hover:border-primary transition-colors">
                                        <div><p className="font-bold">{pkg.name}</p><p className="text-sm text-muted-foreground">${pkg.price_cad} CAD</p></div>
                                        <Button onClick={() => purchasePackage(pkg.id)} disabled={purchasing === pkg.id} className="btn-primary" data-testid={`buy-${pkg.id}`}>
                                            {purchasing === pkg.id ? <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> : <><Plus size={16} className="mr-1" />Buy</>}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-4 text-center">Powered by Stripe. All payments are secure and encrypted.</p>
                        </CardContent>
                    </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.17 }}>
                    <StakingPanel stakingTiers={stakingTiers} stakingPositions={stakingPositions}
                        stakeTier={stakeTier} setStakeTier={setStakeTier}
                        stakeAmount={stakeAmount} setStakeAmount={setStakeAmount}
                        staking={staking} stakeBoltz={stakeBoltz}
                        unstaking={unstaking} unstakeBoltz={unstakeBoltz} user={user} />
                </motion.div>

                {user?.wallet_address && (
                    <>
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                            <OnChainBalance balanceInfo={balanceInfo} loading={loading} fetchWalletData={fetchWalletData} />
                        </motion.div>
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}>
                            <MintToWallet user={user} mintAmount={mintAmount} setMintAmount={setMintAmount} minting={minting} mintTokens={mintTokens} />
                        </motion.div>
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}>
                            <SendBoltz user={user} transferTo={transferTo} setTransferTo={setTransferTo} transferAmount={transferAmount}
                                setTransferAmount={setTransferAmount} transferring={transferring} transferTokens={transferTokens} solanaStatus={solanaStatus} />
                        </motion.div>
                    </>
                )}

                {!user?.wallet_address && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                        <LinkWallet walletAddress={walletAddress} setWalletAddress={setWalletAddress} linking={linking} linkWallet={linkWallet} />
                    </motion.div>
                )}

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                    <Card className="glass-card mb-6">
                        <CardHeader><CardTitle className="flex items-center gap-2"><Info size={20} />Solana Network</CardTitle></CardHeader>
                        <CardContent>
                            <div className="space-y-3 text-sm">
                                {[
                                    ['Network', solanaStatus?.cluster || walletInfo?.cluster || 'Devnet'],
                                    ['Token', 'BOLTZ (6 decimals)'],
                                    ['Connection', solanaStatus?.connected ? `Connected${solanaStatus.version ? ` (v${solanaStatus.version})` : ''}` : 'Offline'],
                                    ['Mode', solanaStatus?.mode === 'live' ? 'Live (On-Chain)' : 'Demo'],
                                ].map(([label, val]) => (
                                    <div key={label} className="flex justify-between">
                                        <span className="text-muted-foreground">{label}</span>
                                        <span className={`font-medium ${label === 'Connection' && solanaStatus?.connected ? 'text-neon-green' : ''} ${label === 'Mode' ? (solanaStatus?.mode === 'live' ? 'text-neon-green' : 'text-neon-gold') : ''}`}>{val}</span>
                                    </div>
                                ))}
                                {solanaStatus?.mint_address && (
                                    <div className="flex justify-between"><span className="text-muted-foreground">Mint</span><span className="font-mono text-xs text-primary truncate max-w-[200px]">{solanaStatus.mint_address}</span></div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                    <Card className="glass-card bg-muted/30">
                        <CardContent className="p-4">
                            <h3 className="font-bold text-sm mb-3 flex items-center"><Lightning size={16} className="mr-2 text-primary" />How Boltz Coins Work</h3>
                            <ul className="text-sm text-muted-foreground space-y-2">
                                <li>• Earn Boltz by completing rides and challenges</li>
                                <li>• Buy Boltz coins with CAD via Stripe</li>
                                <li>• Spend Boltz in the marketplace on gear and bikes</li>
                                <li>• Link a Solana wallet to mint tokens on-chain</li>
                            </ul>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </Layout>
    );
}
